// src/platform/ui/updateDialog.js

import eventBus from '@/platform/core/eventBus'
import logger from '@/platform/core/logger'

import updateManager from '@/platform/pwa/updateManager'


class UpdateDialog {
  constructor() {
    this.initialized = false
    this.visible = false

    this.element = null

    this.unsubscribeAvailable = null
    this.unsubscribeApplying = null
    this.unsubscribeActivated = null

    this.handleUpdateClick =
      this.handleUpdateClick.bind(this)

    this.handleLaterClick =
      this.handleLaterClick.bind(this)
  }


  init() {
    if (
      this.initialized ||
      typeof document === 'undefined'
    ) {
      return
    }

    this.create()


    this.unsubscribeAvailable =
      eventBus.on(
        'platform.update.available',
        () => {
          this.show()
        }
      )


    this.unsubscribeApplying =
      eventBus.on(
        'platform.update.applying',
        () => {
          this.setApplyingState()
        }
      )


    this.unsubscribeActivated =
      eventBus.on(
        'platform.update.activated',
        () => {
          this.hide()
        }
      )


    this.initialized = true

    logger.info(
      'Update dialog initialized.'
    )


    /*
     * Handle the uncommon case where the new
     * service worker was already waiting before
     * this UI initialized.
     */
    if (
      updateManager.hasUpdate()
    ) {
      this.show()
    }
  }


  create() {
    if (
      this.element ||
      typeof document === 'undefined'
    ) {
      return
    }


    const wrapper =
      document.createElement(
        'div'
      )


    wrapper.id =
      'nexgbits-pwa-update-dialog'


    wrapper.setAttribute(
      'role',
      'dialog'
    )


    wrapper.setAttribute(
      'aria-live',
      'polite'
    )


    Object.assign(
      wrapper.style,
      {
        position:
          'fixed',

        right:
          '20px',

        bottom:
          '20px',

        zIndex:
          '100000',

        width:
          'min(420px, calc(100vw - 40px))',

        background:
          '#ffffff',

        borderRadius:
          '12px',

        boxShadow:
          '0 10px 35px rgba(0, 0, 0, 0.22)',

        padding:
          '18px',

        display:
          'none',

        boxSizing:
          'border-box',

        fontFamily:
          'inherit'
      }
    )


    wrapper.innerHTML = `
      <div
        style="
          font-size: 17px;
          font-weight: 600;
          margin-bottom: 8px;
        "
      >
        Update available
      </div>

      <div
        style="
          font-size: 14px;
          line-height: 1.5;
          margin-bottom: 16px;
          color: #555;
        "
      >
        A newer version of NexgbitsIS is available.
        Update now to use the latest version.
      </div>

      <div
        style="
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        "
      >
        <button
          type="button"
          data-action="later"
          style="
            border: 0;
            background: transparent;
            padding: 9px 14px;
            cursor: pointer;
            font-weight: 500;
          "
        >
          Later
        </button>

        <button
          type="button"
          data-action="update"
          style="
            border: 0;
            border-radius: 6px;
            padding: 9px 16px;
            cursor: pointer;
            background: #1976d2;
            color: #ffffff;
            font-weight: 600;
          "
        >
          Update now
        </button>
      </div>
    `


    const updateButton =
      wrapper.querySelector(
        '[data-action="update"]'
      )


    const laterButton =
      wrapper.querySelector(
        '[data-action="later"]'
      )


    updateButton?.addEventListener(
      'click',
      this.handleUpdateClick
    )


    laterButton?.addEventListener(
      'click',
      this.handleLaterClick
    )


    document.body.appendChild(
      wrapper
    )


    this.element =
      wrapper
  }


  handleUpdateClick() {
    logger.info(
      'User requested PWA update.'
    )


    const applied =
      updateManager.applyUpdate()


    if (!applied) {
      logger.warn(
        'Unable to apply PWA update because no waiting worker exists.'
      )

      this.hide()
    }
  }


  handleLaterClick() {
    logger.info(
      'User postponed PWA update.'
    )

    this.hide()
  }


  setApplyingState() {
    if (!this.element) {
      return
    }


    const updateButton =
      this.element.querySelector(
        '[data-action="update"]'
      )


    const laterButton =
      this.element.querySelector(
        '[data-action="later"]'
      )


    if (updateButton) {
      updateButton.disabled =
        true

      updateButton.textContent =
        'Updating...'
    }


    if (laterButton) {
      laterButton.disabled =
        true
    }
  }


  resetButtons() {
    if (!this.element) {
      return
    }


    const updateButton =
      this.element.querySelector(
        '[data-action="update"]'
      )


    const laterButton =
      this.element.querySelector(
        '[data-action="later"]'
      )


    if (updateButton) {
      updateButton.disabled =
        false

      updateButton.textContent =
        'Update now'
    }


    if (laterButton) {
      laterButton.disabled =
        false
    }
  }


  show() {
    if (!this.element) {
      this.create()
    }


    if (!this.element) {
      return
    }


    this.resetButtons()

    this.element.style.display =
      'block'

    this.visible =
      true


    logger.info(
      'PWA update dialog displayed.'
    )
  }


  hide() {
    if (!this.element) {
      return
    }


    this.element.style.display =
      'none'

    this.visible =
      false
  }


  isVisible() {
    return this.visible
  }


  destroy() {
    if (
      this.unsubscribeAvailable
    ) {
      this.unsubscribeAvailable()
      this.unsubscribeAvailable = null
    }


    if (
      this.unsubscribeApplying
    ) {
      this.unsubscribeApplying()
      this.unsubscribeApplying = null
    }


    if (
      this.unsubscribeActivated
    ) {
      this.unsubscribeActivated()
      this.unsubscribeActivated = null
    }


    if (this.element) {
      const updateButton =
        this.element.querySelector(
          '[data-action="update"]'
        )

      const laterButton =
        this.element.querySelector(
          '[data-action="later"]'
        )


      updateButton?.removeEventListener(
        'click',
        this.handleUpdateClick
      )


      laterButton?.removeEventListener(
        'click',
        this.handleLaterClick
      )


      this.element.remove()
    }


    this.element = null
    this.visible = false
    this.initialized = false


    logger.info(
      'Update dialog destroyed.'
    )
  }
}


const updateDialog =
  new UpdateDialog()


export default updateDialog